import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { API_BASE_URL, getImageUrl } from '../../../config';
import AppRequestForm from '../../buildApp/AppRequestForm';
import { toastSuccess, toastError, errorMessage } from '../../../lib/toast';

const ADMIN_CREATE_ERRORS = {
  bad_creator: 'Pick a valid, active employee as the creator.',
  tools_required: 'Add at least one tool.',
  unknown_tool: 'One of the selected tools no longer exists.',
  too_many_tools: 'Too many tools selected.',
  bad_collaborator: 'One of the collaborators is invalid.',
  too_many_collaborators: 'Too many collaborators.',
};

function CreatorPicker({ creator, onPick, onClear }) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const mountedRef = useRef(true);
  useEffect(() => { mountedRef.current = true; return () => { mountedRef.current = false; }; }, []);

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      setSearching(true);
      try {
        // Admin directory (incl. the caller) so an admin can pick themselves as
        // owner — unlike the collaborator picker, which excludes the caller.
        const res = await axios.get(`${API_BASE_URL}/api/admin/users/search`, {
          params: { q: query.trim() }, withCredentials: true,
        });
        if (!cancelled && mountedRef.current) setResults(Array.isArray(res.data) ? res.data : []);
      } catch {
        if (!cancelled && mountedRef.current) setResults([]);
      } finally {
        if (!cancelled && mountedRef.current) setSearching(false);
      }
    };
    const t = setTimeout(run, 200);
    return () => { cancelled = true; clearTimeout(t); };
  }, [query]);

  if (creator) {
    return (
      <div className="ba-picker">
        <div className="ba-picker-head"><span className="ba-picker-label">Creator (owner)</span></div>
        <ul className="ba-chip-row">
          <li className="ba-chip">
            {creator.photo_url
              ? <img className="ba-chip-avatar" src={getImageUrl(creator.photo_url)} alt="" />
              : <span className="ba-chip-avatar ba-chip-avatar--fallback" aria-hidden="true">{(creator.name || '?').charAt(0).toUpperCase()}</span>}
            <span>{creator.name} <span className="ba-collab-result-email">{creator.email}</span></span>
            <button type="button" className="ba-chip-remove" onClick={onClear} aria-label="Change creator">×</button>
          </li>
        </ul>
      </div>
    );
  }

  return (
    <div className="ba-picker">
      <div className="ba-picker-head"><span className="ba-picker-label">Creator (owner)</span></div>
      <input
        type="text" className="search-input" placeholder="Search employees by name or email…"
        value={query} onChange={(e) => setQuery(e.target.value)}
      />
      <div className="ba-collab-results">
        {searching && <p className="ba-collab-empty">Searching…</p>}
        {!searching && results.length === 0 && <p className="ba-collab-empty">No employees match.</p>}
        {!searching && results.map((u) => (
          <button key={u.id} type="button" className="ba-collab-option" onClick={() => onPick(u)}>
            {u.photo_url
              ? <img className="ba-chip-avatar" src={getImageUrl(u.photo_url)} alt="" />
              : <span className="ba-chip-avatar ba-chip-avatar--fallback" aria-hidden="true">{(u.name || '?').charAt(0).toUpperCase()}</span>}
            <span className="ba-collab-result-text">
              <span className="ba-collab-result-name">{u.name}</span>
              <span className="ba-collab-result-email">{u.email}</span>
            </span>
            <span className="ba-collab-add" aria-hidden="true">+</span>
          </button>
        ))}
      </div>
    </div>
  );
}

export default function AdminCreateAppRequest({ open, onClose, onCreated }) {
  const [creator, setCreator] = useState(null);
  const [catalog, setCatalog] = useState([]);
  const [catalogLoading, setCatalogLoading] = useState(false);

  // Reset on close so the next open is clean.
  useEffect(() => { if (!open) { setCreator(null); setCatalog([]); setCatalogLoading(false); } }, [open]);

  // Load the FULL tool/service catalog when the modal opens. It's not scoped to
  // the creator — an admin provisions for a user who may own no tools yet, so
  // they pick from the entire catalog (plus custom rows in the form).
  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    setCatalogLoading(true);
    axios.get(`${API_BASE_URL}/api/admin/tools/catalog-for-apps`, { withCredentials: true })
      .then((res) => { if (!cancelled) setCatalog(Array.isArray(res.data) ? res.data : []); })
      .catch(() => { if (!cancelled) setCatalog([]); })
      .finally(() => { if (!cancelled) setCatalogLoading(false); });
    return () => { cancelled = true; };
  }, [open]);

  const handleSubmit = useCallback(async (payload) => {
    if (!creator) { toastError('Select a creator first.'); throw new Error('no_creator'); }
    try {
      await axios.post(`${API_BASE_URL}/api/admin/app_requests`,
        { ...payload, creator_id: creator.id }, { withCredentials: true });
      toastSuccess(`Request created for ${creator.name}.`);
      onCreated && onCreated();
      onClose && onClose();
    } catch (err) {
      toastError(errorMessage(err, "Couldn't create the request. Try again.", ADMIN_CREATE_ERRORS));
      throw err;
    }
  }, [creator, onCreated, onClose]);

  // Close on Escape (drawer convention). Ignored when not open.
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') onClose && onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  return (
    <>
      <div
        className={`aca-drawer-backdrop${open ? ' is-open' : ''}`}
        onClick={onClose}
        aria-hidden="true"
      />
      <aside
        className={`aca-drawer${open ? ' is-open' : ''}`}
        role="dialog"
        aria-modal="true"
        aria-label="Create app request for an employee"
      >
        <header className="adm-modal-head">
          <h3>Create app request for an employee</h3>
          <button type="button" className="adm-modal-close" onClick={onClose} aria-label="Close">×</button>
        </header>
        <div className="aca-drawer-body">
          {!open ? null : !creator ? (
            <CreatorPicker creator={creator} onPick={setCreator} onClear={() => setCreator(null)} />
          ) : catalogLoading ? (
            <>
              <CreatorPicker creator={creator} onPick={setCreator} onClear={() => setCreator(null)} />
              <p className="ba-empty">Loading the tool catalog…</p>
            </>
          ) : (
            <AppRequestForm
              catalog={catalog}
              headerSlot={<CreatorPicker creator={creator} onPick={setCreator} onClear={() => setCreator(null)} />}
              onSubmit={handleSubmit}
              onSubmitted={async () => { /* close handled in handleSubmit */ }}
              excludeCollaboratorIds={creator ? [creator.id] : null}
              collaboratorSearchUrl="/api/admin/users/search"
            />
          )}
        </div>
      </aside>
    </>
  );
}
